import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Sidebar from '@/components/feature/Sidebar';
import Footer from '@/components/feature/Footer';
import Reveal from '@/components/feature/Reveal';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';

export default function Scan() {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Sticky bar background on scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="flex min-h-screen bg-background-50">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <div className="flex-1 flex flex-col min-w-0">
        <main className="flex-1 overflow-auto">
          {/* Sticky top bar */}
          <div
            className={`sticky top-0 z-40 px-3 md:px-6 py-2.5 md:py-3 flex items-center justify-between gap-3 transition-all duration-300 ${
              scrolled
                ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
                : 'bg-background-50 border-b border-background-200/70'
            }`}
          >
            <div className="flex items-center gap-2 md:gap-3 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden btn-press w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg text-foreground-600 hover:bg-background-100 active:bg-background-200 transition-colors cursor-pointer flex-shrink-0"
              >
                <i className="ri-menu-line text-lg md:text-xl" />
              </button>
              <h1 className="font-heading text-sm md:text-lg lg:text-xl font-bold text-foreground-950 truncate">
                {t('hero.title')}
              </h1>
            </div>
            <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
              <LanguageSwitcher variant="light" />
            </div>
          </div>

          {/* Hero banner */}
          <section className="relative w-full h-0 md:h-[360px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20smartphone%20scanning%20a%20QR%20code%20on%20a%20stylish%20cafe%20table%20beside%20a%20warm%20cup%20of%20artisan%20coffee%20and%20fresh%20pastries%2C%20cozy%20warm%20lighting%2C%20premium%20restaurant%20ambiance%2C%20appetizing%20and%20tech-savvy%20food%20photography%2C%20rich%20caramel%20and%20gold%20tones&width=1600&height=700&seq=scan-hero-01&orientation=landscape"
              alt="Scan QR Code"
              className="w-full h-full object-cover object-center hidden md:block"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/40 via-foreground-950/30 to-foreground-950/60 hidden md:block" />
            <div className="absolute inset-0 hidden md:flex items-center justify-center px-4">
              <div className="text-center max-w-2xl w-full">
                <span className="inline-flex items-center gap-2 text-accent-200 text-xs font-medium tracking-widest uppercase mb-3">
                  <i className="ri-qr-scan-line text-base" />
                  BIOLA
                </span>
                <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-3">
                  {t('scan.title')}
                </h1>
                <p className="text-background-100 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
                  {t('scan.subtitle')}
                </p>
              </div>
            </div>
          </section>

          {/* QR Code Content */}
          <section className="w-full px-4 md:px-6 py-8 md:py-14 bg-background-50">
            <div className="max-w-lg mx-auto">
              {/* Mobile title */}
              <div className="md:hidden text-center mb-6">
                <span className="inline-flex items-center gap-2 text-accent-600 text-xs font-medium tracking-widest uppercase mb-2">
                  <i className="ri-qr-scan-line text-base" />
                  BIOLA
                </span>
                <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">
                  {t('scan.title')}
                </h1>
                <p className="text-foreground-600 text-sm max-w-md mx-auto leading-relaxed">
                  {t('scan.subtitle')}
                </p>
              </div>

              <Reveal>
                <div className="bg-background-100 border border-background-200 rounded-2xl p-6 md:p-10 text-center">
                  {/* QR Code Image */}
                  <div className="mx-auto w-[280px] h-[280px] md:w-[360px] md:h-[360px] rounded-xl overflow-hidden bg-white border border-background-200 p-3 shadow-sm">
                    <img
                      src="https://storage.helloreaddy.io/project_files/6023cef2-5a1a-4888-bed9-d31e135f8721/c4be3948-b363-435d-a3a5-a7347bc45d0b_compressed_qrcode_biolacafe.com.webp"
                      alt="BIOLA Cafe QR Code"
                      className="w-full h-full object-contain"
                    />
                  </div>

                  {/* Description */}
                  <div className="mt-6 md:mt-8 space-y-3">
                    <p className="text-foreground-800 text-sm md:text-base font-medium leading-relaxed">
                      {t('scan.qrDesc')}
                    </p>
                    <p className="text-foreground-500 text-xs md:text-sm leading-relaxed">
                      {t('scan.qrHint')}
                    </p>
                  </div>

                  {/* CTA */}
                  <div className="mt-6 md:mt-8">
                    <a
                      href="https://biolacafe.com"
                      target="_blank"
                      rel="noreferrer noopener"
                      className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 text-sm font-medium transition-all btn-press whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-global-line" />
                      {t('scan.visitSite')}
                    </a>
                  </div>
                </div>
              </Reveal>
            </div>
          </section>

          <Footer />
        </main>
      </div>
    </div>
  );
}